import React from 'react';
import { toast } from 'sonner';
import { FirebaseError } from 'firebase/app';
import { useNavigate } from '@tanstack/react-router';
import type { AgentRequest, AlternateAgentInfo, Chart, PatientInfo1 } from '@usacsmideo/types';
import { patientAuthorizationConsentVersion } from '@usacsmideo/types';
import { Button } from '../buttons';
import { AgentConsentBlock } from './AgentConsentBlock';
import { auth } from '../../constants/firebaseModular';
import useChart from '../../hooks/useChart';
import { useMyAgents } from '../../hooks/useAgentRequests';
import { useUpdateChartSection } from '../../hooks/useUpdateChartSection';
import { inviteAgent, archiveAgentRequest } from '../../data/agents/handleAgentRequest';
import { resolveSlotStatus, type SlotStatus } from '../../data/agents/agentSlotConvergence';

/**
 * ECN invite ↔ chart convergence (CU-86aj2q4fc) — chart-slot-driven alternate
 * management, shared by the My MIDEO Family hub and the careTeam alternateAgents
 * screen so the two never drift.
 *
 * The model is INVERTED from the old status-list: the chart slot
 * (ALTERNATE_AGENT_INFO_1 / _2) is the primary entity, and the agentRequest is
 * matched to it by the slot's stamped `agentRequestId` (validity by email match).
 * Each slot row shows the charted person's name + a single gated action derived
 * from `resolveSlotStatus`:
 *   empty      → "Add an alternate" (cross-link to the chart form)
 *   incomplete → "Add details to invite" (cross-link; gate not yet met)
 *   invitable  → "Invite to receive alerts" (assent → send + stamp the slot)
 *   orphaned   → re-invite alert (email diverged from a live invite)
 *   invited    → status label, no duplicate invite
 *
 * Inviting an alternate forms the same BILATERAL consent pair as the HCA, so the
 * patient's in-session assent is captured before send (parity with the HCA path).
 *
 * "Remove" archives any live invite (audit trail preserved) AND clears the slot's
 * chart record — by OVERWRITING it to empty, the same setDoc(merge) mutation any
 * chart edit uses (NOT a hard delete: no new destructive/retention pattern). The
 * slot then reads "empty" and is free for a new person. Confirmed up front.
 *
 * Page-concern separation (redesign §3.2) is preserved: demographics are edited
 * ONLY in the chart form; the invite fires here and stamps the link.
 */

const MAX_ALTERNATES = 2;

const ALT_SLOTS = [
  { key: 'ALTERNATE_AGENT_INFO_1', label: 'Alternate Agent 1' },
  { key: 'ALTERNATE_AGENT_INFO_2', label: 'Alternate Agent 2' },
] as const;

type AltSlotKey = (typeof ALT_SLOTS)[number]['key'];

/**
 * An empty alternate record. Removing a slot overwrites it with this (same
 * setDoc(merge) path as any edit) so every field is blanked and `agentRequestId`
 * is dropped — the slot then resolves to "empty" and is reusable.
 */
const EMPTY_ALTERNATE: AlternateAgentInfo = {
  agent: '',
  email: '',
  primaryPhone: '',
  street: '',
  city: '',
  state: '',
  ZIP: '',
  country: '',
  agentToPatient: '',
  patientToAgent: '',
  agentRequestId: '',
};

function statusLabel(r: AgentRequest): string {
  switch (r.status) {
    case 'waiting':
      return 'Invitation sent — awaiting acceptance';
    case 'accepted':
      return 'Active — receiving alerts';
    default:
      return r.status;
  }
}

export function AlternateAgentsManager() {
  const uid = auth.currentUser?.uid ?? '';
  const navigate = useNavigate();
  const { partition, hasResolved } = useMyAgents();
  const chartQuery = useChart(uid, { enabled: !!uid });
  const chart = chartQuery.data as Chart | undefined;
  const { mutate: stampChart, mutateAsync: writeChart } = useUpdateChartSection();

  const [busySlot, setBusySlot] = React.useState<AltSlotKey | null>(null);
  // The slot currently arming an invite (showing its assent confirmation).
  const [assentingSlot, setAssentingSlot] = React.useState<AltSlotKey | null>(null);
  const [assented, setAssented] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Patient names for the P1 consent template. PATIENT_INFO_1 is the canonical
  // patient profile (same source the HCA path + card render read).
  const patientInfo = chart?.PATIENT_INFO_1 as PatientInfo1 | undefined;
  const patientFullName = `${patientInfo?.firstName ?? ''} ${patientInfo?.lastName ?? ''}`.trim();
  const patientFirstName = patientInfo?.firstName ?? '';

  // Index every alternate request by its doc id so a slot's stamped
  // `agentRequestId` resolves in O(1). We consider ALL requests (incl. terminal)
  // so resolveSlotStatus can tell "no link" from "link to an archived request".
  const requestsById = React.useMemo(() => {
    const map = new Map<string, AgentRequest>();
    partition.all
      .filter((r) => r.agentType === 'alternateAgent')
      .forEach((r) => map.set(r.id, r));
    return map;
  }, [partition.all]);

  const goToChartForm = () => {
    navigate({
      to: '/$patientId/careTeam/alternateAgents',
      params: { patientId: uid },
    });
  };

  /**
   * Send the invite for a complete slot, then stamp the returned id onto it.
   * When re-inviting an ORPHANED slot, `staleRequestId` is the old (now
   * email-mismatched) live request — we ARCHIVE it first so the abandoned email
   * can no longer accept and the slot never carries two live invites.
   */
  const handleInviteSlot = async (
    slot: AltSlotKey,
    record: AlternateAgentInfo,
    staleRequestId?: string,
  ) => {
    // Affirmation re-check (parity with the HCA path + defense-in-depth behind the
    // disabled Send button and the P2.1 server gate). Runs BEFORE any archive so an
    // un-affirmed re-invite mints/archives nothing.
    if (!assented) {
      setError('Please confirm the authorization before sending the invitation.');
      return;
    }
    setBusySlot(slot);
    setError(null);
    try {
      if (staleRequestId) {
        await archiveAgentRequest(staleRequestId);
      }
      const res = await inviteAgent({
        agentType: 'alternateAgent',
        recipientEmail: record.email.trim(),
        recipientName: record.agent.trim() || undefined,
        // Affirmation gate (P2.1/P2.2): patient ticked the P1 consent (guarded
        // above). Version from the shared builder the backend drift-checks against.
        consentAffirmed: true,
        consentVersion: patientAuthorizationConsentVersion(),
      });
      // Stamp the link onto the chart slot. We write the full (typed) record with
      // agentRequestId merged in, rather than a loose partial — useUpdateChartSection
      // uses setDoc(merge:true), so this preserves any unrelated fields too.
      // Stamp on BOTH outcomes: the request doc exists even when the email
      // failed to send, so linking it lets the orphan/re-invite affordance
      // manage + resend it (and avoids a duplicate request on retry).
      const stamped: AlternateAgentInfo = { ...record, agentRequestId: res.id };
      stampChart({
        patientId: uid,
        docData:
          slot === 'ALTERNATE_AGENT_INFO_1'
            ? { ALTERNATE_AGENT_INFO_1: stamped }
            : { ALTERNATE_AGENT_INFO_2: stamped },
      });
      setAssentingSlot(null);
      setAssented(false);
      // `ok` reflects DELIVERY, not doc creation (MIDEO-UNIFIED-18). A failed
      // send must not read as clean success — tell the patient the agent was
      // NOT emailed so they can verify the address and resend.
      if (res.ok === false || res.deliveryStatus === 'failed') {
        setError(
          `Invitation created, but we couldn't email ${record.email.trim()}. ` +
            'Please verify the address and use Re-invite to try again.',
        );
        toast.error("We couldn't deliver the invitation email.");
      } else {
        toast.success('Invitation sent.');
      }
    } catch (err) {
      // A malformed email is rejected by the cloud function with
      // invalid-argument (the hard gate behind the form validation).
      if (err instanceof FirebaseError && err.code === 'functions/invalid-argument') {
        setError('That email address looks invalid. Please check it and try again.');
      } else {
        setError('We could not send the invitation. Please try again or contact support.');
      }
    } finally {
      setBusySlot(null);
    }
  };

  /**
   * Remove: archive any live invite (audit trail kept) and clear the slot by
   * OVERWRITING it to an empty record — the same setDoc(merge) path as any chart
   * edit, not a hard delete. The slot then resolves to "empty" and is reusable.
   * Confirmed up front because it stops alerts and clears saved details.
   */
  const handleRemove = async (slot: AltSlotKey, name: string, liveRequestId?: string) => {
    const confirmed = window.confirm(
      `Remove ${name || 'this alternate agent'}? This stops any alerts to them and clears their ` +
        `saved details from your chart. You can designate someone new in this slot afterward.`,
    );
    if (!confirmed) return;
    setBusySlot(slot);
    setError(null);
    try {
      if (liveRequestId) {
        await archiveAgentRequest(liveRequestId);
      }
      await writeChart({
        patientId: uid,
        docData:
          slot === 'ALTERNATE_AGENT_INFO_1'
            ? { ALTERNATE_AGENT_INFO_1: EMPTY_ALTERNATE }
            : { ALTERNATE_AGENT_INFO_2: EMPTY_ALTERNATE },
      });
    } catch {
      setError('We could not remove this agent. Please try again or contact support.');
    } finally {
      setBusySlot(null);
    }
  };

  if (!hasResolved) {
    return <p className="text-gray-600">Loading…</p>;
  }

  const slots = ALT_SLOTS.map((s) => {
    const record = chart?.[s.key] as AlternateAgentInfo | undefined;
    const linked = record?.agentRequestId ? requestsById.get(record.agentRequestId) : undefined;
    const status: SlotStatus = resolveSlotStatus(record, linked);
    return { ...s, record, status };
  });

  // Cap: an "empty" slot can only be filled if we're under the designated count.
  const designatedCount = slots.filter((s) => s.status.state !== 'empty').length;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold text-gray-800">
        Alternate Agents ({designatedCount}/{MAX_ALTERNATES})
      </h2>
      <p className="text-sm text-gray-600">
        Alternate agents can act on your behalf if your Healthcare Agent is unavailable. You may
        designate up to two. Add their details in the chart, then invite them to receive alerts.
      </p>

      {error && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {slots.map(({ key, label, record, status }) => {
        // Don't render a third empty row beyond the cap, and only render the FIRST
        // empty slot as an "add" affordance (slots are positional).
        if (status.state === 'empty') {
          const isFirstEmpty = slots.find((s) => s.status.state === 'empty')?.key === key;
          if (!isFirstEmpty || designatedCount >= MAX_ALTERNATES) return null;
          return (
            <Button
              key={key}
              type="button"
              id={`add-alternate-${key}`}
              textContent="Add an alternate agent"
              onClick={goToChartForm}
            />
          );
        }

        const name = record?.agent || label;
        const isArming = assentingSlot === key;

        return (
          <div
            key={key}
            className="flex w-full flex-col gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex w-full items-center justify-between">
              <div className="text-left">
                <p className="font-semibold text-sky-800">{name}</p>
                <SlotStatusLine status={status} />
              </div>
              <div className="flex gap-2">
                <SlotAction
                  status={status}
                  busy={busySlot === key}
                  arming={isArming}
                  onAddDetails={goToChartForm}
                  onArmInvite={() => {
                    setAssented(false);
                    setError(null);
                    setAssentingSlot(key);
                  }}
                  onRemove={() => handleRemove(key, name, status.request?.id)}
                />
              </div>
            </div>

            {/* Consent authorization (P1 template) + affirmation — shown only while
                arming an invite for this slot. Signed copy dictates "Send Invite" +
                "I agree to the terms and conditions" (in AgentConsentBlock). */}
            {isArming && (
              <div className="flex flex-col gap-3">
                <AgentConsentBlock
                  vars={{
                    patientFullName,
                    patientFirstName,
                    // Full agent name string in the first-name slot (no split
                    // first/last on the chart) — grammatical in P1, mirrors backend.
                    agentFirstName: record?.agent?.trim() ?? '',
                    agentRole: 'alternate agent',
                  }}
                  affirmed={assented}
                  onAffirmChange={setAssented}
                  disabled={busySlot === key}
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    id="send-invite-alternate"
                    textContent="Send Invite"
                    working={busySlot === key}
                    workingText="Sending…"
                    disabled={busySlot === key || !assented}
                    onClick={() =>
                      record &&
                      handleInviteSlot(
                        key,
                        record,
                        status.state === 'orphaned' ? status.request?.id : undefined,
                      )
                    }
                  />
                  <Button
                    type="button"
                    id="cancel-invite-alternate"
                    textContent="Cancel"
                    disabled={busySlot === key}
                    classNameOverride="my-2 rounded-md border border-gray-300 px-4 py-2 text-xl text-gray-700 hover:bg-gray-100 sm:px-8 sm:py-3"
                    onClick={() => {
                      setAssentingSlot(null);
                      setAssented(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** The descriptive line under the slot name, by convergence state. */
function SlotStatusLine({ status }: { status: SlotStatus }) {
  switch (status.state) {
    case 'incomplete':
      return (
        <p className="text-sm text-amber-700">
          Details incomplete — finish their chart record before inviting.
        </p>
      );
    case 'invitable':
      return <p className="text-sm text-gray-600">Ready to invite to receive alerts.</p>;
    case 'orphaned':
      return (
        <p className="text-sm text-amber-700">
          Their email changed — this invite no longer matches. Re-invite to restore alerts.
        </p>
      );
    case 'invited':
      return (
        <p className="text-sm text-gray-600">{status.request ? statusLabel(status.request) : ''}</p>
      );
    default:
      return null;
  }
}

/**
 * The gated action(s) for a slot. The primary action depends on convergence state;
 * a destructive "Remove" is always available on a non-empty slot so the patient can
 * free it. While arming an invite, the primary action is hidden (the inline assent
 * panel owns Send/Cancel).
 */
function SlotAction({
  status,
  busy,
  arming,
  onAddDetails,
  onArmInvite,
  onRemove,
}: {
  status: SlotStatus;
  busy: boolean;
  arming: boolean;
  onAddDetails: () => void;
  onArmInvite: () => void;
  onRemove: () => void;
}) {
  return (
    <>
      {!arming && status.state === 'incomplete' && (
        <Button
          type="button"
          id="add-details-alternate"
          textContent="Add details to invite"
          onClick={onAddDetails}
        />
      )}
      {!arming && status.state === 'invitable' && (
        <Button
          type="button"
          id="invite-alternate"
          textContent="Invite to receive alerts"
          disabled={busy}
          onClick={onArmInvite}
        />
      )}
      {!arming && status.state === 'orphaned' && (
        <Button
          type="button"
          id="reinvite-alternate"
          textContent="Re-invite"
          disabled={busy}
          onClick={onArmInvite}
        />
      )}
      <Button
        type="button"
        id={status.request ? `remove-${status.request.id}` : 'remove-alternate'}
        textContent="Remove"
        working={busy}
        workingText="Removing…"
        disabled={busy}
        classNameOverride="my-2 rounded-md border border-gray-300 px-4 py-2 text-xl text-gray-700 hover:bg-gray-100 sm:px-8 sm:py-3"
        onClick={onRemove}
      />
    </>
  );
}

export default AlternateAgentsManager;
